// PRIMARY / PRIMARY_DARK / SECONDARY / DISABLED_GREEN follow the store's brand
// colours (Admin → Store Setup) once they're known; see applyBrandColors.
export const Colors = {
  PRIMARY: "#018752",
  PRIMARY_DARK: "#016B42",
  SECONDARY: "#0079C2",
  WHITE: "#FFFFFF",
  BLACK: "#000000",
  ACCENT_GRAY: "#D9D9D9",
  ACCENT_GRAY_DISABLED: "#B3B3B3",
  BACKGROUND_GRAY: "#F8F8F8",
  BACKSHADE_GRAY: "#F4F4F4",
  MAIN_GRAY: "#515151",
  TEXT_GRAY_DARK: "#4D4D4D",
  BROWN: "#930000",
  COLOR_BUTTON_BORDER: "#808080",
  LIGHT_YELLOW: "#F9FFB0",
  NOTIFIC_RED: "#EFD8D8",
  NOTIFIC_GREEN: "#D8EFD9",
  ERROR_RED: "#EE0000",
  SUCCESS_GREEN: "#00AA00",
  PINTAG5: "#FFAEB5",
  REPORT_BLUE: "#A1CEF5",
  REPORT_GRAY: "#515151",
  REPORT_GREEN: "#00796B",
  REPORT_DARK_GREEN: "#018752",
  REPORT_WHITE: "#F6F6F6",
  PINTAG_LIGHT_ORANGE: "#FEEFE5",
  DISABLED_GREEN: "#73B79C",
};

export const DEFAULT_BRAND = { primary: "#018752", secondary: "#0079C2" };

// Last applied brand colours, so the app starts in them before the store's
// setup has loaded (and module-level styles read the right values)
const BRAND_KEY = "storeBrandColors";

const isHex = (v?: string | null): v is string => !!v && /^#[0-9A-Fa-f]{6}$/.test(v);

const toRgb = (hex: string) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));

const toHex = (rgb: number[]) =>
  `#${rgb.map((c) => Math.round(Math.min(255, Math.max(0, c))).toString(16).padStart(2, "0")).join("")}`.toUpperCase();

/** The colour mixed towards black (amount 0–1). */
export const shade = (hex: string, amount: number) => toHex(toRgb(hex).map((c) => c * (1 - amount)));

/** The colour mixed towards white (amount 0–1): light backgrounds of the brand colour. */
export const tint = (hex: string, amount: number) => toHex(toRgb(hex).map((c) => c + (255 - c) * amount));

/** rgba() of a #RRGGBB colour. */
export const withAlpha = (hex: string, alpha: number) => `rgba(${toRgb(hex).join(",")},${alpha})`;

/** The brand primary at the given opacity, e.g. focus rings. */
export const primaryAlpha = (alpha: number) => withAlpha(Colors.PRIMARY, alpha);

/** The brand secondary at the given opacity. */
export const secondaryAlpha = (alpha: number) => withAlpha(Colors.SECONDARY, alpha);

/** A light background of the brand primary (amount = how close to white). */
export const primaryTint = (amount: number) => tint(Colors.PRIMARY, amount);

const setCssVars = () => {
  if (typeof document === "undefined") return;
  const root = document.documentElement.style;
  root.setProperty("--brand-primary", Colors.PRIMARY);
  root.setProperty("--brand-primary-rgb", toRgb(Colors.PRIMARY).join(" "));
  root.setProperty("--brand-primary-dark-rgb", toRgb(Colors.PRIMARY_DARK).join(" "));
  root.setProperty("--brand-secondary", Colors.SECONDARY);
  root.setProperty("--brand-secondary-rgb", toRgb(Colors.SECONDARY).join(" "));
};

/**
 * Sets the brand colours (null = Presto defaults) on Colors and the
 * --brand-* CSS variables Tailwind's `brand` colours use. Returns whether
 * anything changed.
 */
export const applyBrandColors = (primary?: string | null, secondary?: string | null): boolean => {
  const nextPrimary = (isHex(primary) ? primary : DEFAULT_BRAND.primary).toUpperCase();
  const nextSecondary = (isHex(secondary) ? secondary : DEFAULT_BRAND.secondary).toUpperCase();
  const changed = nextPrimary !== Colors.PRIMARY.toUpperCase() || nextSecondary !== Colors.SECONDARY.toUpperCase();
  Colors.PRIMARY = nextPrimary;
  Colors.PRIMARY_DARK = nextPrimary === DEFAULT_BRAND.primary ? "#016B42" : shade(nextPrimary, 0.2);
  Colors.SECONDARY = nextSecondary;
  Colors.DISABLED_GREEN = nextPrimary === DEFAULT_BRAND.primary ? "#73B79C" : tint(nextPrimary, 0.45);
  setCssVars();
  try {
    localStorage.setItem(BRAND_KEY, JSON.stringify({ primary: nextPrimary, secondary: nextSecondary }));
  } catch {
    // Private mode etc.: colours still apply for this session
  }
  return changed;
};

(() => {
  try {
    const saved = JSON.parse(localStorage.getItem(BRAND_KEY) || "null");
    applyBrandColors(saved?.primary, saved?.secondary);
  } catch {
    setCssVars();
  }
})();
